import dynamic from "next/dynamic";

import isEmpty from "lodash/isEmpty";

import classNames from "classnames";

import React from "react";

import type { LinkRouteDefaults } from "@stadium/shared/src/components/link-with-lang";

import { ControlContextSlugs } from "@stadium/shared/src/helpers/plasmic/static-slices";

import { BrandCarouselProps } from "@components/swagmagic/dynamic/slices/brand-carousel";
import { CatalogEntriesCarouselProps } from "@components/swagmagic/dynamic/slices/catalog-entries-carousel";
import { CatalogEntriesGridProps } from "@components/swagmagic/dynamic/slices/catalog-entries-grid";
import { CatalogEntriesPillCarouselProps } from "@components/swagmagic/dynamic/slices/catalog-entries-pill-carousel";
import { CategoryListProps } from "@components/swagmagic/dynamic/slices/category-list";

import {
  CE_05,
  CE_08,
  CE_09,
  CE_12,
  CategoryVariant,
  CUSTOM,
  DETAILED_CATEGORY_CARD_VARIANT,
  DynamicSlicesData,
  ViewType,
} from "@helpers/plasmic/slices";

import { getCategoriesToRender } from "@helpers/plasmic/catalog-entries";

import { DEFAULT_CATALOG_ENTRIES_PROPS } from "@constants/plasmic/slices";
import { ALL_PRODUCTS, ALL_SUBCATEGORY_CODE } from "@constants/routes";

import styles from "./main.module.scss";

const CatalogEntriesCarousel = dynamic(
  () => import("@components/swagmagic/dynamic/slices/catalog-entries-carousel"),
) as React.ComponentType<CatalogEntriesCarouselProps>;
const CatalogEntriesGrid = dynamic(
  () => import("@components/swagmagic/dynamic/slices/catalog-entries-grid"),
) as React.ComponentType<CatalogEntriesGridProps>;

const BrandCarousel = dynamic(
  () => import("@components/swagmagic/dynamic/slices/brand-carousel"),
) as React.ComponentType<BrandCarouselProps>;

const CategoryList = dynamic(
  () => import("@components/swagmagic/dynamic/slices/category-list"),
) as React.ComponentType<CategoryListProps>;

const CatalogEntriesPillCarousel = dynamic(
  () => import("@components/swagmagic/dynamic/slices/catalog-entries-pill-carousel"),
) as React.ComponentType<CatalogEntriesPillCarouselProps>;

export type ControlContextData = {
  slugs: ControlContextSlugs;
  renderedSlugs: ControlContextSlugs;
  categorySlugs: ControlContextSlugs;
  brandSlugs?: ControlContextSlugs;
};

export interface CatalogEntriesProps {
  setControlContextData?: (data: ControlContextData) => void;
  className?: string;
  variant: CategoryVariant;
  viewType: ViewType;
  title?: string;
  description?: string;
  btnText?: string;
  btnLinkType: string;
  btnLinkOtherUrl?: string;
  btnLinkCategorySlug?: string;
  linkToNewTab: boolean;
  showSubcategories: boolean;
  visibleCategoryCount?: number;
  visibleBrandCount?: number;
  hideCategories: string[];
  orderCategories: string[];
  countryCode: string;
  customCategories?: any[];
  categorySlug?: string;
  dynamicSlicesData?: DynamicSlicesData;
  hideCategorySelector?: boolean;
  controlContextData: ControlContextData;
  /** From store menu selector; threaded to slice `LinkWithLang` instances */
  linkRouteDefaults?: LinkRouteDefaults;
}

const CatalogEntries = (
  props: CatalogEntriesProps = DEFAULT_CATALOG_ENTRIES_PROPS as CatalogEntriesProps,
) => {
  if (!props.dynamicSlicesData) {
    return null;
  }

  const {
    className,
    viewType,
    variant,
    customCategories,
    orderCategories,
    categorySlug,
    dynamicSlicesData,
    controlContextData,
  } = props;

  const {
    store,
    menu,
    parentCategories: parentCategoriesData,
    allCategories: allCategoriesData,
    menuMapping,
    countryIso,
  } = dynamicSlicesData;

  if (viewType === CUSTOM && variant !== CE_05 && isEmpty(customCategories)) {
    return null;
  }

  const { categoriesToRender, parentCategories } = getCategoriesToRender({
    ...props,
    allCategoriesData,
    parentCategoriesData,
    skipTitleHumanization: menu?.skipTitleHumanization || false,
  });

  if (isEmpty(categoriesToRender)) {
    return null;
  }
  const commonProps = {
    ...props,
    store,
    storeNumber: store?.number,
    storeCode: store?.code,
    countryCode: countryIso,
  };

  const carouselKey = `${variant}-${categoriesToRender.length}-${orderCategories?.length || 0}`;

  let content: React.ReactNode;
  let isCarouselVariant = false;

  switch (variant) {
    case CE_05: {
      isCarouselVariant = true;
      content = (
        <BrandCarousel
          key={carouselKey}
          {...commonProps}
          categories={categoriesToRender}
          menu={menu}
          menuMapping={menuMapping}
          controlContextData={controlContextData}
        />
      );
      break;
    }

    case CE_08: {
      const leftSectionCategories = parentCategories?.filter((category) => {
        return (
          !(category.code === ALL_PRODUCTS && category.itemCode === ALL_SUBCATEGORY_CODE) &&
          category.code !== categorySlug
        );
      });

      content = (
        <CatalogEntriesGrid
          key={carouselKey}
          {...commonProps}
          categories={categoriesToRender}
          title={props.title}
        >
          <CategoryList
            categories={leftSectionCategories || []}
            linkRouteDefaults={props.linkRouteDefaults}
          />
        </CatalogEntriesGrid>
      );
      break;
    }

    case CE_09: {
      content = (
        <CatalogEntriesGrid key={carouselKey} {...commonProps} categories={categoriesToRender} />
      );
      break;
    }

    case CE_12:
      isCarouselVariant = true;
      content = (
        <CatalogEntriesPillCarousel
          key={carouselKey}
          {...commonProps}
          categoriesToRender={categoriesToRender}
        />
      );
      break;
    default: {
      isCarouselVariant = true;
      content = (
        <CatalogEntriesCarousel
          key={carouselKey}
          {...commonProps}
          categoriesToRender={categoriesToRender}
        />
      );
      break;
    }
  }

  const isDetailedCategoryCard = DETAILED_CATEGORY_CARD_VARIANT.includes(variant);
  const containerClass =
    isCarouselVariant && !isDetailedCategoryCard
      ? styles.carouselSlicesContainer
      : styles.primarySlicesContainer;

  return <div className={classNames(containerClass, className)}>{content}</div>;
};

export default CatalogEntries;
